export type Stage = "prep" | "sewing" | "finish";
export type WorkerKind = "human" | "robot";
export type WorkshopFixtureKind = "rack" | "obstacle";

/** Image-relative coordinates (0–1), independent of the displayed image size. */
export interface WorkshopFixture {
  id: string;
  kind: WorkshopFixtureKind;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  createdAt: string;
  updatedAt: string;
}

export type SaveWorkshopFixtureInput = Pick<WorkshopFixture, "kind" | "name" | "x" | "y" | "width" | "height">;

export interface WorkshopLayout {
  floorPlan: { width: number; height: number; widthCm: number | null; heightCm: number | null; updatedAt: string } | null;
  fixtures: WorkshopFixture[];
}
export interface WorkshopLayoutArchive {
  version: 1;
  floorPlan: { contentType: "image/png" | "image/jpeg" | "image/webp"; imageBase64: string; width: number; height: number; widthCm: number | null; heightCm: number | null } | null;
  fixtures: Array<SaveWorkshopFixtureInput & {
    id: string;
    locations: Array<Pick<WorkshopRackLocation, "id" | "parentId" | "kind" | "name" | "processLabel" | "sortOrder" | "flowRole" | "flowPriority">>;
  }>;
}
export type WorkshopRackLocationKind = "tier" | "basket" | "outside";
export interface WorkshopWorkpiece {
  id: string;
  returnId: string;
  issuanceId: string;
  locationId: string | null;
  projectName: string;
  taskName: string;
  workerName: string;
  quantity: number;
  normalQuantity: number | null;
  defectiveQuantity: number | null;
  status: "qc_pending" | "qc_passed" | "needs_correction";
  placement: "stored" | "outbound" | "unplaced";
  updatedAt: string;
}
export interface WorkshopRackLocation {
  id: string;
  rackId: string;
  parentId: string | null;
  kind: WorkshopRackLocationKind;
  name: string;
  processLabel: string;
  flowRole: import("./workpieceFlow.js").WorkpieceFlowRole | null;
  flowPriority: number | null;
  sortOrder: number;
}
export type WorkshopRackStockKind = "workpiece" | "material" | "other";
export interface WorkshopRackStock {
  id: string;
  locationId: string;
  projectName: string;
  itemName: string;
  code: string;
  kind: WorkshopRackStockKind;
  quantity: number;
  updatedAt: string;
}
export interface WorkshopRackStatus {
  rack: WorkshopFixture;
  locations: WorkshopRackLocation[];
  stock: WorkshopRackStock[];
  workpieces: WorkshopWorkpiece[];
  templateLocked: boolean;
  trackedWorkpieces?: import("./workpieceTracking.js").WorkpiecePage<import("./workpieceTracking.js").WorkpieceInventoryItem>;
  trackedSummary?: import("./workpieceTracking.js").WorkpieceRackSummary[];
}
export type SaveWorkshopRackLocationInput = Pick<WorkshopRackLocation, "kind" | "parentId" | "name" | "processLabel">;
/** Current placement groups, not historical movements or a truncated lot page. */
export interface RackOverviewContent {
  key: string;
  locationId: string;
  source: "tracked" | "workpiece" | "stock";
  projectId: string | null;
  projectName: string;
  name: string;
  state: string;
  quantity: number;
  batchCount: number;
}
export interface RackOverviewRack {
  rack: WorkshopFixture;
  locations: WorkshopRackLocation[];
  contents: RackOverviewContent[];
}
export interface RackOverview {
  racks: RackOverviewRack[];
  generatedAt: string;
}
export type SaveWorkshopRackStockInput = Pick<WorkshopRackStock, "locationId" | "projectName" | "itemName" | "code" | "kind" | "quantity">;
export type WorkerGroup = "sewing" | "finish" | "side_job" | "qc" | "operations";
export type Difficulty = "easy" | "normal" | "hard" | "expert";
export type PlanMethod = "manual" | "rule" | "ai";
export type PlanRunStatus = "draft" | "confirmed";
export type PlanItemStatus = "planned" | "in_progress" | "completed";
export type SettlementSource = "piecework" | "part_time" | "contract";
export type SettlementRecordStatus = "pending" | "batched" | "paid";
export type SettlementRecordKind = "base" | "adjustment" | "manual";
export type SettlementAdjustmentKind = "deduction" | "addition";
export type SettlementBatchStatus = "prepared" | "paid";
export type SettlementBatchMessageStatus = "prepared" | "sent" | "failed";
export type ContractCalculationType = "fixed" | "quantity";
export type IssuanceStatus =
  | "issued"
  | "in_progress"
  | "inspection_pending"
  | "completed";

export type WorkerAttendanceStatus = "present" | "pending" | "departed" | "absent";

export interface WorkerAttendance {
  workerName: string;
  status: WorkerAttendanceStatus;
  arrivalTime: string | null;
  departureTime: string | null;
}

export interface CommuteAttendanceResponse {
  date: string;
  updatedAt: string;
  workers: WorkerAttendance[];
}

export interface CommuteHistoryQuery {
  from: string;
  to: string;
  workerName?: string;
}

export interface CommuteHistoryResponse {
  from: string;
  to: string;
  updatedAt: string;
  workerNames: string[];
  records: Array<{ date: string; workerName: string; arrivalTime: string | null; departureTime: string | null }>;
}

export interface AuthUser {
  id: string;
  username: string;
  name: string;
  role: "admin" | "editor" | "viewer" | "worker";
  approved: boolean;
  tabs: string[];
  writeTabs: string[];
}

export interface AuthSession {
  token: string;
  expiresAt: string;
  user: AuthUser;
}

export interface AuthMember {
  id: number;
  role: "admin" | "editor" | "viewer";
  status: "pending" | "approved" | "suspended";
  sessionVersion: number;
  requestedAt: string;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: { id: number; username: string; nickname: string; is_active: boolean } | null;
}

export interface AuthAuditEntry {
  id: string;
  username: string;
  action: string;
  outcome: "success" | "denied" | "error";
  reason: string;
  ipAddress: string;
  createdAt: string;
}

export interface ActivityHistoryEntry {
  id: string;
  resourceType: "issuances" | "returns" | "inspections" | "manual_settlement";
  resourceId: string;
  action: "insert" | "update" | "delete";
  actorKind: "user" | "app" | "kiosk" | "system";
  actorId: string;
  actorName: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
  projectName: string | null;
  taskName: string | null;
  workerName: string | null;
}

export interface LegacyWorkRecord {
  id: string;
  sourceKey: string;
  namespace: "sewing" | "prep";
  operation: "upsert" | "delete";
  record: unknown;
  receivedAt: string;
  processedAt: string | null;
  processedBy: string | null;
}

export interface KioskDeviceCredential {
  id: string;
  name: string;
  tokenHint: string;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  lastUsedIp: string;
  revokedAt: string | null;
  revokedBy: string;
}

export const stageLabels: Record<Stage, string> = {
  prep: "밑작업",
  sewing: "재봉작업",
  finish: "완성작업"
};

export interface MaterialItem {
  id: string;
  name: string;
  ready: boolean;
}

export interface SubtaskRoundPlan {
  roundId: string;
  roundNumber: number;
  plannedQuantity: number;
  dueDate: string | null;
  issuedQuantity: number;
  returnedQuantity: number;
  normalQuantity: number;
  progress: number;
}

export interface Subtask {
  id: string;
  projectId: string;
  stage: Stage;
  /** Sewing output with no downstream production sewing task. */
  isSkinOutput?: boolean;
  name: string;
  plannedQuantity: number;
  unitPrice: number;
  difficulty: Difficulty;
  inspectionRequired: boolean;
  sequence: number;
  roundId?: string | null;
  roundNumber?: number | null;
  dueDate?: string | null;
  /** TMS 차수별 계획. 불출·회수·완료 누계는 앞 차수부터 순서대로 배분됩니다. */
  roundPlans?: SubtaskRoundPlan[];
  allocationMode?: "free" | "fixed" | "multiple";
  fixedQuantity?: number;
  /** 기존 시스템의 완료 기록을 조회하기 위한 공정이며 새 불출·플래닝 대상이 아닙니다. */
  historicalOnly?: boolean;
  /** An allocation has been registered for this subtask. */
  allocationRegistered?: boolean;
  sourceSteps?: Array<{ sourceTaskId: number; name: string; effortShare: number; position: number;
    unitPrice?: number | null; missing?: boolean; sourceChanges?: string[] }>;
  /** The latest sum of linked TMS process prices; null when a source is missing. */
  sourceUnitPriceTotal?: number | null;
  sourceReadiness?: {
    status: "known" | "unknown";
    quantity: number | null;
    reasons: string[];
  } | null;
  /** Cumulative cut-product capacity by linked TMS process. Undefined is legacy;
   * [] means the TMS process has no direct pattern requirement. Never a free-stock count. */
  patternPreparations?: Array<{ poolId: string; sourceTaskId: number; quantity: number | null; reasons: string[] }>;
  splitFamilyId?: string | null;
  predecessorIds: string[];
  normalQuantity: number;
  defectiveQuantity: number;
  /** 불량(고칠 수 없음) 수량 — 검수의 scrap_quantity 합. defectiveQuantity 는 수정가능(다시 고치러 가는 수)이다(2026-10-20). */
  scrapQuantity?: number;
  issuedQuantity: number;
  returnedQuantity: number;
  progress: number;
}

export interface SourceProcessOption {
  id: number;
  name: string;
  stage: Stage;
  unitPrice: number;
  plannedQuantity: number;
  predecessorIds: number[];
  /** 이 공정이 내놓는 작업물 이름(TMS task_part, 없으면 공정 이름) — 다음 공정의 predecessorWorkpieces 이름과 같다. */
  workpieceName?: string;
  /** quantity = 완성품 1개당 필요한 재단물 수(TMS required_quantity_per_unit, 차수별로 다르면 큰 값). */
  requiredPatterns?: Array<{ patternKey: string; name: string; quantity?: number }>;
  predecessorWorkpieces?: Array<{ processId: number; name: string }>;
  roundPlans: Array<{ roundNumber: number; quantity: number }>;
}

export interface Project {
  id: string;
  name: string;
  /** Only present in the project information catalog; never enables production. */
  catalogState?: "completed" | "archived" | "unavailable";
  /** TMS is the system of record unless this project was explicitly created locally. */
  sourceType: "tms" | "temporary";
  /** Why this local-only project exists; shown to operators so it is not mistaken for TMS data. */
  temporaryReason?: string;
  /** The name was chosen locally, so a TMS import leaves it alone. */
  nameCustomized?: boolean;
  client: string;
  dueDate: string;
  deliveryQuantity: number;
  /** 한 번에 불출하는 묶음 수(벌크). 프로젝트에 하나, 불출 칸의 기본 수량·「/ 벌크」가 된다. 없으면 null. */
  bulkQuantity?: number | null;
  status: "active" | "completed";
  /** 원본 시스템이 말하는 단계(양산·생산확정 …). 우리 status("운영 대상인가")와 다른 축이다. */
  sourceStatus?: string;
  createdAt: string;
  imported?: import("./tmsTypes.js").ImportedProjectInfo;
  materials: MaterialItem[];
  subtasks: Subtask[];
  progress: number;
}

export interface ArchivedProjectSummary {
  id: string;
  name: string;
  client: string;
  dueDate: string;
  sourceType: "tms" | "temporary";
  archivedAt: string;
  subtaskCount: number;
}

export interface Worker {
  id: string;
  name: string;
  displayedName: string;
  kind: WorkerKind;
  primaryStage: Stage;
  grade: string;
  workDays: string[];
  /** Minutes after local midnight; absent means the legacy 09:00 shift. */
  workStartMinutes?: number;
  predictedHourlyWage: number;
  /** 근무 시작~종료 사이에 빠지는 휴게 시간(분). weekdayMinutes에서는 이미 빠져 있다. */
  breakMinutes: number;
  weekdayMinutes: number;
  weekendMinutes: number;
  settlementType: "piece" | "hourly" | "none";
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
  phone: string;
  active: boolean;
  planningPriority?: number;
  /** 공장의 자리 번호. 자리가 고정이고 사람이 거기 앉는다. */
  seatNo?: number | null;
  residentRegistrationNumberMasked: string;
  address: string;
  workGroups: WorkerGroup[];
  notes: string;
  identityPhoto: WorkerIdentityPhoto | null;
  identityPhotos: WorkerIdentityPhoto[];
  /** 관리자만 조회할 수 있는 RFID 카드 식별자입니다. 현장 bootstrap에는 포함하지 않습니다. */
  rfidTag: string;
}

export type SettlementWorkerOption = Pick<Worker, "id" | "displayedName" | "kind" | "active">;

/**
 * 현장 화면에 공개할 수 있는 최소 작업자 정보입니다.
 * 급여·연락처·주소·주민등록번호·신분증 정보는 의도적으로 포함하지 않습니다.
 */
export interface PublicWorker {
  id: string;
  displayedName: string;
  kind: WorkerKind;
  active: boolean;
  workDays: string[];
  settlementType: "piece" | "hourly" | "none";
}

export interface FinishTabletWorker extends PublicWorker {
  hasFinishGroup: boolean;
  /** 현재 태블릿 작업 종류에 배정된 작업자인지 여부입니다. */
  hasStageGroup?: boolean;
}

export interface WorkerIdentityPhoto {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  createdAt: string;
}

/** Robot-specific metadata. Scheduling fields live on the linked worker resource. */
export interface Robot {
  id: string;
  workerId: string;
  name: string;
  displayedName: string;
  modelName: string;
  serialNumber: string;
  defaultUnitsPerHour: number;
  active: boolean;
  workDays: string[];
  workStartMinutes: number;
  weekdayMinutes: number;
  weekendMinutes: number;
  /** 로봇도 어느 공정에 서는지가 있다 — 사람과 같은 workers 행에 적히고 같은 표에 나란히 선다. */
  workGroups: WorkerGroup[];
  planningPriority: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageGroupMember {
  workerId: string;
  workerName: string;
  phone: string;
  active: boolean;
}

export interface MessageGroup {
  id: string;
  name: string;
  position: number;
  members: MessageGroupMember[];
  createdAt: string;
  updatedAt: string;
}

export interface WorkshopHoliday {
  id: string;
  holidayDate: string;
  dates: string[];
  startTime: string;
  endTime: string;
  name: string;
  createdBy: string;
  createdAt: string;
}

export interface PublicHoliday {
  holidayDate: string;
  name: string;
}

export interface PublicHolidayResponse {
  configured: boolean;
  holidays: PublicHoliday[];
}

export interface PartTimeWorkRecord {
  id: string;
  workerId: string;
  workerName: string;
  workDate: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  hourlyWage: number;
  workMinutes: number;
  estimatedAmount: number;
  memo: string;
  createdBy: string;
  createdAt: string;
}

export type EmploymentContractType = "regular" | "fixed_term" | "freelance";

export interface EmploymentContract {
  id: string;
  workerId: string;
  workerName: string;
  contractType: EmploymentContractType;
  startDate: string;
  endDate: string | null;
  workDays: string[];
  startTime: string;
  endTime: string;
  monthlyWage: number;
  /** 위 금액이 어느 기간의 보수인지. 도급제는 계약에 금액을 적지 않는다. */
  wagePeriod: "monthly" | "weekly" | "daily" | "hourly" | "contract";
  memo: string;
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkerTaskEfficiency {
  id: string;
  workerId: string;
  workerName: string;
  workerKind: WorkerKind;
  subtaskId: string;
  subtaskName: string;
  projectId: string;
  stage: Stage;
  unitsPerHour: number;
  /** Directly entered standard minutes for one unit; null when not entered. */
  smvMinutes?: number | null;
  /** Effective SMV used by the planner, including predictions from completed work. */
  estimatedSmvMinutes?: number | null;
  estimateSource?: "entered" | "observed_pair" | "task" | "name" | "category" | "global" | "legacy" | "unavailable";
  sampleCount?: number;
  source: "calculated" | "manual";
  updatedBy: string;
  updatedAt: string;
}

/** Evaluation belongs to a worker and original process, not to a replaceable plan ID. */
export interface WorkAllocation {
  processKey: string;
  kind: "trial" | "bulk";
  phase?: 1 | 2;
  conditional?: boolean;
}

export interface WorkPlanItem {
  allocation?: WorkAllocation;
  id: string;
  /** Read-only timeline projection of an actual issuance, never a stored plan item. */
  displaySource?: "actual";
  /** 수정 요청으로 같은 작업자에게 다시 나간 불출(원 불출의 수정가능 수량). 막대에 「재불출」이 붙는다(2026-10-26 지윤 「수정 요청 = 그 작업자에게 재불출」). */
  rework?: boolean;
  planRunId: string;
  projectId: string;
  projectName: string;
  workDate: string;
  workerId: string;
  workerName: string;
  workerKind: WorkerKind;
  subtaskId: string;
  subtaskName: string;
  stage: Stage;
  quantity: number;
  instruction: string;
  sequence: number;
  startAt: string | null;
  endAt: string | null;
  plannedMinutes: number | null;
  workSegments?: Array<{ startAt: string; endAt: string }>;
  splitOriginId?: string | null;
  manuallyEdited: boolean;
  status: PlanItemStatus;
  issuanceId: string | null;
  /* 회수가 남긴 건. 블록이 이것을 알아야 그 자리에서 검수까지 간다. */
  remainingQuantity?: number | null;
  returnId?: string | null;
  returnQuantity?: number | null;
  returnedByWorkerId?: string | null;
  /* 마지막 회수 시각. 다 돌아오고 검수만 남은 막대는 계획한 끝이 아니라 여기서 끝난다. */
  returnedAt?: string | null;
  inspectedAt?: string | null;
  defectiveQuantity?: number | null;
  workStartedAt: string | null;
  workCompletedAt: string | null;
  createdAt: string;
}

export interface WorkPlanRun {
  id: string;
  projectId: string;
  projectName: string;
  projectIds: string[];
  projectNames: string[];
  name: string;
  method: PlanMethod;
  stage: Stage | null;
  status: PlanRunStatus;
  startDate: string;
  endDate: string;
  createdBy: string;
  createdAt: string;
  confirmedAt: string | null;
  algorithm: string | null;
  calculationMs: number | null;
  estimatedCompletionAt: string | null;
  items: WorkPlanItem[];
}

export type PlannerHistoryBlock = Pick<WorkPlanItem, "id" | "planRunId" | "projectId" | "projectName" | "workerId" | "workerName" | "subtaskId" | "subtaskName" | "quantity" | "startAt" | "endAt" | "instruction" | "manuallyEdited" | "status" | "workStartedAt" | "workCompletedAt"> & { taskNumber?: number; projectColorIndex?: number };
export interface PlannerBlockChange { before: PlannerHistoryBlock | null; after: PlannerHistoryBlock | null }
export interface PlannerHistoryEntry {
  triggerReason?: string;
  summaryStatus?: "legacy" | "pending" | "retrying" | "processing" | "completed" | "failed" | "blocked" | "skipped";
  summaryConfigured?: boolean;
  summaryAttempts?: number;
  summaryError?: string;
  summaryHttpStatus?: number | null;
  summaryRetryAt?: string | null;
  summaryProvider?: string;
  summaryModel?: string;
  summaryLastAttemptAt?: string | null;
  id: number;
  runId: string;
  createdAt: string;
  actor: string;
  title: string;
  summary: string;
  reason: string;
  added: number;
  removed: number;
  updated: number;
  beforeCompletionAt: string | null;
  afterCompletionAt: string | null;
}
export interface PlannerHistoryDetail { entry: PlannerHistoryEntry; changes: PlannerBlockChange[]; nextOffset: number | null }

export interface CorrectionWorkPeriod {
  startedAt: string;
  endedAt: string | null;
}

export interface ReturnRecord {
  id: string;
  issuanceId: string;
  quantity: number;
  recordedByWorkerId: string;
  recordedByName: string;
  returnedAt: string;
  inspectionRequired: boolean;
  inspectionId: string | null;
  /** QC 시작 표시는 선택적이며 검수 결과 입력의 선행 조건이 아닙니다. */
  inspectionStartedAt?: string | null;
  normalQuantity: number;
  defectiveQuantity: number;
  scrapQuantity?: number;
  defectTypes?: string[];
  /** 남아 있는 수정 가능 불량의 유형별 수량. 합계는 defectiveQuantity 이하여야 합니다. */
  defectTypeQuantities?: DefectTypeQuantity[];
  /** 유형별 수량이 있는 경우 현재 수정 가능 불량 수량이며, 목록이 비면 null입니다. */
  defectTypeQuantityBasis?: number | null;
  inspectedByName: string | null;
  inspectedAt: string | null;
  /** 동일한 불출 건의 직전 검수 회차. null이면 최초 회수입니다. */
  correctionOfReturnId?: string | null;
  /** QC 결과 수정·직접 수정 완료 후에도 보존하는 수정 작업 기간입니다. */
  correctionPeriods?: CorrectionWorkPeriod[];
}

export interface ReturnCancellationPreview {
  returnId: string;
  issuanceId: string;
  quantity: number;
  correction: boolean;
  inspectionWillBeVoided: boolean;
  effects: string[];
  blockers: string[];
  previewToken: string;
}

export type LabelPrintStatus = "queued" | "sending" | "sent" | "failed" | "unknown" | "skipped";
export interface LabelPrintJob {
  id: string;
  status: LabelPrintStatus;
  lastError: string;
  updatedAt: string;
}

export interface Issuance {
  /** 명시적으로 수정 요청한 원 불출. 수정 작업도 일반 불출·회수 상태로 관리한다. */
  reworkSourceIssuanceId?: string | null;
  reworkTransferredQuantity?: number;
  reworkOriginalDefectiveQuantity?: number | null;
  cancelledAt?: string | null;
  allocation?: WorkAllocation;
  cancelledQuantity?: number;
  id: string;
  projectId: string;
  projectName: string;
  subtaskId: string;
  subtaskName: string;
  stage: Stage;
  workerId: string;
  workerName: string;
  workerKind: WorkerKind;
  quantity: number;
  /** 일반 불출 순서. 수정 작업은 원본 차수를 상속하며 전량 취소는 null입니다. */
  assignmentNumber: number | null;
  /** 기존 시스템에서 이미 완료된 기록을 재현한 불출입니다. */
  historicalOnly?: boolean;
  /** 완성 태블릿에서 완료 시 수량을 확정하는 불출인지 여부입니다. */
  quantityPending?: boolean;
  returnedQuantity: number;
  normalQuantity: number;
  defectiveQuantity: number;
  /** 불량(고칠 수 없음) 수량 — 검수의 scrap_quantity 합. defectiveQuantity 는 수정가능(다시 고치러 가는 수)이다(2026-10-20). */
  scrapQuantity?: number;
  remainingQuantity: number;
  pendingInspectionQuantity: number;
  /** 현재 작업자가 수정한 뒤 다시 회수해야 하는 수량입니다. */
  correctionQuantity: number;
  correctionOfReturnId?: string | null;
  status: IssuanceStatus;
  /** 정산 묶음에 포함되어 더 이상 수정할 수 없는 기록입니다. */
  settlementLocked?: boolean;
  /** 정산할 때 고정한 단가. null 이면 아직 정산 전이라 하위작업의 지금 단가를 쓴다. */
  settlementUnitPrice?: number | null;
  completedAt: string | null;
  issuedAt: string;
  /** 마지막 회수(또는 전량 취소) 시각. 검수 시각은 작업 종료로 보지 않습니다. */
  workEndedAt?: string | null;
  /** 출석 근무 구간과 작업 구간이 실제로 겹친 초. null이면 출석 추적 대상이 아닙니다. */
  actualWorkSeconds?: number | null;
  /** 출석 기록과 불출~종료 구간의 교집합. 외출·점심·퇴근 구간은 서로 다른 항목으로 저장됩니다. */
  actualWorkSegments?: ActualWorkSegment[] | null;
  /** 실제 작업 구간을 출퇴근 원본과 마지막으로 동기화한 시각입니다. */
  actualWorkSyncedAt?: string | null;
  createdBy: string;
  returns: ReturnRecord[];
  labelPrint: LabelPrintJob | null;
}

export interface ActualWorkSegment {
  id: string;
  startedAt: string;
  endedAt: string;
  workedSeconds: number;
}

export interface ActualWorkSnapshot {
  issuanceId: string;
  actualWorkSegments: ActualWorkSegment[] | null;
  actualWorkSeconds: number | null;
  syncedAt: string;
}

export type WorkReservationStatus = "reserved" | "started" | "cancelled";
export interface WorkReservation {
  reworkSourceIssuanceId?: string | null;
  id: string;
  projectId: string;
  projectName: string;
  subtaskId: string;
  subtaskName: string;
  stage: Stage;
  workerId: string;
  workerName: string;
  quantity: number;
  status: WorkReservationStatus;
  issuanceId: string | null;
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
}

export interface DefectTypeQuantity {
  name: string;
  quantity: number;
}

export interface Inspection {
  id: string;
  returnId: string;
  normalQuantity: number;
  defectiveQuantity: number;
  scrapQuantity?: number;
  defectTypes?: string[];
  /** 남아 있는 수정 가능 불량의 유형별 수량. 합계는 defectiveQuantity 이하여야 합니다. */
  defectTypeQuantities?: DefectTypeQuantity[];
  /** 유형별 수량이 있는 경우 현재 수정 가능 불량 수량이며, 목록이 비면 null입니다. */
  defectTypeQuantityBasis?: number | null;
  inspectorWorkerId: string | null;
  inspectorName: string;
  inspectedAt: string;
}

export interface PartTimeSettlement {
  id: string;
  workerId: string;
  workerName: string;
  workDate: string;
  amount: number;
  description: string;
  createdBy: string;
  createdAt: string;
}

export interface SettlementContract {
  id: string;
  workerId: string;
  workerName: string;
  title: string;
  /** 어느 하위공정의 일인가. 도급은 1건 = 1공정이라 적을 수 있다. */
  subtaskName: string;
  startDate: string;
  endDate: string;
  /** 언제 돈이 나가는가. 일한 기간의 끝과 같을 이유가 없다. 비어 있으면 endDate를 쓴다. */
  settlementDate: string;
  calculationType: ContractCalculationType;
  quantity: number;
  unitAmount: number;
  amount: number;
  description: string;
  createdBy: string;
  createdAt: string;
}

export interface SettlementContractAttachment {
  id: string;
  contractId: string;
  filename: string;
  contentType: string;
  size: number;
  createdAt: string;
}

export interface SettlementRecord {
  id: string;
  source: SettlementSource;
  workerId: string;
  workerName: string;
  /** 현재 계좌 필수값 확인 결과. 비활성 여부와 무관하며 계좌 원문은 노출하지 않는다. */
  workerBankAccountReady?: boolean;
  settlementDate: string;
  /** 정산 기준일과 별개인 작업 기간. 구형·조정 기록에는 없을 수 있다. */
  workStartedAt?: string | null;
  workCompletedAt?: string | null;
  title: string;
  recordKind: SettlementRecordKind;
  adjustmentKind: SettlementAdjustmentKind | null;
  amount: number;
  grossAmount: number;
  /** 별도로 등록한 공제·추가 정산 항목의 금액. 일반 항목은 0입니다. */
  adjustmentAmount: number;
  /** 수동 정산 항목의 부호 있는 세후 조정액. 다른 원장 유형에는 없습니다. */
  manualAmount?: number | null;
  manualContent?: string | null;
  taxableGrossAmount: number;
  withholdingAmount: number;
  payableAmount: number;
  quantity: number | null;
  unitAmount: number | null;
  projectId: string | null;
  projectName: string | null;
  subtaskId: string | null;
  subtaskName: string | null;
  referenceId: string;
  status: SettlementRecordStatus;
  batchId: string | null;
  batchedAt: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface SettlementBatchTransfer {
  id: string;
  workerId: string;
  workerName: string;
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
  amount: number;
  memo: string;
  sequence: number;
}

export interface SettlementMessageWorkItem {
  settlementRecordId: string;
  source: SettlementSource;
  settlementDate: string;
  title: string;
  recordKind: SettlementRecordKind;
  adjustmentKind: SettlementAdjustmentKind | null;
  projectName: string | null;
  subtaskName: string | null;
  quantity: number | null;
  unitAmount: number | null;
  grossAmount: number;
  adjustmentAmount: number;
  manualAmount?: number | null;
  taxableGrossAmount: number;
  withholdingAmount: number;
  payableAmount: number;
}

export interface SettlementPendingInspection {
  projectId: string;
  projectName: string;
  subtaskId: string;
  subtaskName: string;
  quantity: number;
}

export interface SettlementBatchMessage {
  id: string;
  batchId: string;
  workerId: string;
  workerName: string;
  receiver: string;
  message: string;
  header?: string;
  footer?: string;
  workItems: SettlementMessageWorkItem[];
  /** 조회 시점의 미지급 검수 대기 내역. 발송 원문과 정산액에는 영향을 주지 않습니다. */
  pendingInspections?: SettlementPendingInspection[];
  status: SettlementBatchMessageStatus;
  providerMessageIds: string[];
  sentAt: string | null;
  errorMessage: string | null;
  sequence: number;
}

export interface SettlementBatch {
  id: string;
  name: string;
  status: SettlementBatchStatus;
  recordIds: string[];
  recordCount: number;
  negativeWorkerNames?: string[];
  grossAmount: number;
  deductionAmount: number;
  additionAmount: number;
  taxableGrossAmount: number;
  withholdingAmount: number;
  payableAmount: number;
  transfers: SettlementBatchTransfer[];
  messages: SettlementBatchMessage[];
  createdBy: string;
  createdAt: string;
  completedBy: string | null;
  completedAt: string | null;
}

export interface SettlementBatchSummary {
  id: string;
  name: string;
  status: SettlementBatchStatus;
  source: SettlementSource;
  recordCount: number;
  recipientCount: number;
  /** 이 묶음으로 준비된 정산 문자 수와, 그중 발송이 끝난 수입니다. */
  messageCount: number;
  messageSentCount: number;
  messageAttentionCount?: number;
  grossAmount: number;
  deductionAmount: number;
  additionAmount: number;
  taxableGrossAmount: number;
  withholdingAmount: number;
  payableAmount: number;
  /** 작업자별 공제 결과가 음수여서 이체액을 0원으로 제한한 경우입니다. */
  negativeWorkerNames?: string[];
  createdBy: string;
  createdAt: string;
  completedBy: string | null;
  completedAt: string | null;
}

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
  /** 거른 조건에 맞는 전체 건수. 쪽 번호를 받아 둔 줄이 아니라 전체 위에서 세기 위한 값이다. */
  total?: number;
}

export type SettlementSort = "date" | "date_asc" | "worker" | "worker_desc" | "work_time" | "work_time_asc";

export interface SettlementRecordPage extends CursorPage<SettlementRecord> {
  /** 전체 조회 범위의 선택지이며 현재 페이지나 작업자 필터에 제한되지 않는다. */
  filterOptions?: {
    workerId: Array<{ value: string; label: string }>;
    projectName: Array<{ value: string; label: string }>;
    subtaskName: Array<{ value: string; label: string }>;
  };
}

export interface ProjectWorkRecord {
  id: string;
  /** 전량 취소 여부. 초기 조회 범위 밖의 과거 기록에도 표시·필터를 적용한다. */
  isCancelled?: boolean;
  projectId: string;
  projectName: string;
  subtaskId: string;
  subtaskName: string;
  stage: Stage;
  workerId: string;
  workerName: string;
  issuedAt: string;
  returnedAt: string | null;
  /** 출퇴근 기록과 작업 구간이 겹친 초. 미동기화는 undefined, 추적 대상이 아니면 null. */
  actualWorkSeconds?: number | null;
  defectiveQuantity?: number;
  scrapQuantity?: number;
  reworkSourceIssuanceId?: string | null;
  quantity: number;
  quantityPending: boolean;
  /** 전량 취소 기록은 차수 없음(null). */
  assignmentNumber: number | null;
}

/** Names and stable IDs only; includes historical/archived records outside bootstrap. */
export interface ProjectWorkRecordFilterOptions {
  workers: Array<{ id: string; name: string }>;
  projects: Array<{ id: string; name: string }>;
  subtasks: Array<{ id: string; name: string; projectId: string; projectName: string }>;
}

export interface SettlementStats {
  piecework: { amount: number; count: number };
  part_time: { amount: number; count: number };
  contract: { amount: number; count: number };
}

export interface PlannerProgress {
  kind: "automatic";
  phase: "preparing" | "searching" | "validating" | "applying";
  startedAt: string;
  budgetMs: number;
  detail?: string;
}
export interface PlannerState {
  operatingRules?: { revision: number; appliedRevision: number | null; status: "current" | "outdated" };
  progress?: PlannerProgress | null;
  automaticBudgetMs?: number;
  enabled: boolean;
  runId: string | null;
  projectIds: string[];
  startDate: string;
  revision: number;
  generation: number;
  status: "computing" | "current";
  error: string | null;
  updatedAt: string | null;
}

export interface PlanningDispatchGroupMember {
  subtaskId: string;
  subtaskName: string;
  partName: string | null;
  sharePercent: number | null;
}

export interface PlanningDispatchGroup {
  id: string;
  projectId: string;
  kind: "merge" | "split";
  createdAt: string;
  members: PlanningDispatchGroupMember[];
}

export interface PlanningForecastWarning {
  issuanceId: string;
  workerId: string;
  subtaskId: string;
  canSetSmv: boolean;
  workerName: string;
  projectName: string;
  subtaskName: string;
  reason: string;
}

export interface ZeroPriceTaskDefinition {
  id: string;
  name: string;
  stage: Stage;
  unitPrice: number;
  historicalOnly: boolean;
  projectId: string;
  projectName: string;
}

export interface BootstrapResponse {
  /** 완료 프로젝트의 실제 기록은 보존하되 플래너 표시에서는 제외한다. */
  completedProjectIds?: string[];
  planningForecastWarnings?: PlanningForecastWarning[];
  planningSettings?: { gapMinutes: number; sameTaskGapMinutes?: number; projectSwitchExtraMinutes?: number; minimumImprovementMinutes?: number; bulkTargetMinutes?: number };
  planner?: PlannerState;
  projects: Project[];
  workers: Worker[];
  robots: Robot[];
  issuances: Issuance[];
  workReservations: WorkReservation[];
  inspections: Inspection[];
  planRuns: WorkPlanRun[];
  workerTaskEfficiencies: WorkerTaskEfficiency[];
  partTimeSettlements: PartTimeSettlement[];
  contracts: SettlementContract[];
  /** TMS에 연결하지 못해 로컬 스냅샷으로 응답한 경우의 사유입니다. */
  tmsOffline?: string;
  settlements: SettlementRecord[];
  settlementBatches: SettlementBatch[];
  workshopHolidays: WorkshopHoliday[];
  partTimeWorkRecords: PartTimeWorkRecord[];
  employmentContracts: EmploymentContract[];
  messageGroups: MessageGroup[];
  planningDispatchGroups: PlanningDispatchGroup[];
}

/** Authoritative, project-scoped read model returned atomically with a write. */
export interface WorkMutationSnapshot {
  projectId: string;
  projectCompleted?: boolean;
  project: Project | null;
  issuances: Issuance[];
  workReservations: WorkReservation[];
  inspections: Inspection[];
}
export interface WorkMutationResult {
  snapshot?: WorkMutationSnapshot;
  /** Authoritative plan block returned by a work action, without waiting for replanning. */
  planItem?: WorkPlanItem;
}

/** 현장 작업 전용 읽기 모델. 관리자 bootstrap과 별도 API에서 제공합니다. */
export interface FieldBootstrapResponse {
  workers: PublicWorker[];
  issuances: Issuance[];
}

export type FinishTaskType = "turning" | "stuffing" | "ladder_stitch" | "other";

export interface FinishTabletTask {
  id: string;
  projectId: string;
  projectName: string;
  name: string;
  /** Project process order; older cached snapshots fall back to task list order. */
  sequence?: number;
  type: FinishTaskType;
  availableQuantity: number;
  issueQuantity: number;
  fixedQuantity: number | null;
  plannedQuantity: number;
  normalQuantity: number;
}

export interface FinishTabletIssuance {
  id: string;
  workerId: string;
  stage: Stage;
  projectId: string;
  projectName: string;
  subtaskId: string;
  subtaskName: string;
  quantity: number;
  quantityPending: boolean;
  returnedQuantity: number;
  remainingQuantity: number;
  correctionQuantity: number;
  correctionOfReturnId: string | null;
  availableQuantity: number;
  issuedAt: string;
}

export interface TabletProjectDeadline {
  projectId: string;
  projectName: string;
  dueDate: string;
  quantity: number | null;
  roundNumber: number | null;
  completed: boolean;
}

export interface TabletDeadlineProject {
  id: string;
  name: string;
  rounds: Array<{ number: number | null; dueDate: string | null; quantity: number | null; completed: boolean }>;
  stages: {
    sewing: { taskCount: number; plannedQuantity: number; completedQuantity: number; progress: number | null };
    finish: { taskCount: number; plannedQuantity: number; completedQuantity: number; progress: number | null };
  };
}

/** 완성 작업 태블릿 전용 최소 읽기 모델입니다. */
export interface FinishTabletBootstrapResponse {
  workers: FinishTabletWorker[];
  tasks: FinishTabletTask[];
  activeIssuances: FinishTabletIssuance[];
  reservations: WorkReservation[];
  deadlines: TabletProjectDeadline[];
  deadlineProjects: TabletDeadlineProject[];
}

export interface ReturnWorkpiecePlacement {
  parentLocationName?: string;
  workerId?: string;
  workerName?: string;
  returnId?: string;
  updatedAt?: string;
  revision?: number;
  storageMode?: import("./returnStorage.js").ReturnStorageInput["mode"];
  typeName?: string;
  workState?: import("./workpieceTracking.js").WorkpieceWorkState;
  workpieceId: string;
  quantity: number;
  projectName: string;
  taskName: string;
  rackId: string | null;
  rackName: string | null;
  locationId: string | null;
  locationName: string | null;
}

export interface FinishTabletCompletionResult {
  workpiecePlacements?: ReturnWorkpiecePlacement[];
  id: string;
  placement?: ReturnWorkpiecePlacement;
  notification: {
    status: "sent" | "skipped" | "failed";
    reason?: "disabled" | "excluded_worker" | "missing_phone" | "send_failed";
    providerMessageIds: string[];
  };
}

export interface FinishTabletRfidResult {
  workerId: string;
  displayedName: string;
  state: "idle" | "working";
  hasStageGroup: boolean;
}

export interface SaveWorkerInput {
  name: string;
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
  residentRegistrationNumber?: string;
  address: string;
  phone: string;
  active: boolean;
  workGroups: WorkerGroup[];
  notes: string;
  workDays: string[];
  startTime: string;
  endTime: string;
  breakMinutes: number;
  planningPriority: number;
  settlementType: "piece" | "hourly";
  rfidTag?: string;
}

export interface SaveRobotInput {
  name: string;
  modelName: string;
  serialNumber: string;
  defaultUnitsPerHour: number;
  active: boolean;
  /** 로봇도 어느 공정을 맡는지가 있다 — RC1은 밑작업, RM은 재봉이다. */
  workGroups: WorkerGroup[];
  workDays: string[];
  startTime: string;
  endTime: string;
  planningPriority: number;
  notes: string;
}

export interface SaveMessageGroupInput {
  name: string;
  workerIds: string[];
}

export interface SendAnnouncementMessageInput {
  workerIds: string[];
  message: string;
}

export interface SendAnnouncementMessageResult {
  sent: number;
  failed: number;
  skipped: number;
  failures: Array<{ workerId: string; workerName: string; message: string }>;
}

export interface CreateWorkshopHolidayInput {
  dates: string[];
  startTime: string;
  endTime: string;
  name: string;
  createdBy: string;
}

export interface SavePartTimeWorkRecordInput {
  workerId: string;
  workDate: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  hourlyWage: number;
  memo: string;
  createdBy: string;
}

export interface SaveEmploymentContractInput {
  workerId: string;
  contractType: EmploymentContractType;
  startDate: string;
  endDate: string | null;
  workDays: string[];
  startTime: string;
  endTime: string;
  monthlyWage: number;
  wagePeriod: "monthly" | "weekly" | "daily" | "hourly" | "contract";
  memo: string;
  active: boolean;
  createdBy: string;
}

export interface UpdateWorkerBankAccountInput {
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
}

export interface CreateSettlementBatchInput {
  name: string;
  settlementRecordIds: string[];
  createdBy: string;
}

export interface SaveSettlementAdjustmentInput {
  source: SettlementSource;
  workerId: string;
  settlementDate: string;
  kind: SettlementAdjustmentKind;
  amount: number;
  memo: string;
  createdBy: string;
}

export interface SaveManualSettlementInput {
  workerId: string;
  amount: number;
  content: string;
  createdBy: string;
}

export interface CompleteSettlementBatchInput {
  completedBy: string;
  sendMessages?: boolean;
}

export interface UpdateSettlementBatchMessageInput {
  receiver: string;
  message: string;
  header?: string;
  footer?: string;
}

export interface CompleteSettlementBatchResult {
  sentCount: number;
  alreadySentCount: number;
}

export interface CreateProjectInput {
  name: string;
  client: string;
  dueDate: string;
  deliveryQuantity: number;
  temporaryReason?: string;
  materials: Array<{ name: string }>;
  subtasks: Array<{
    stage: Stage;
    name: string;
    plannedQuantity: number;
    unitPrice: number;
    difficulty: Difficulty;
    inspectionRequired: boolean;
  }>;
}

export interface UpdateTemporaryProjectInput {
  name: string;
  client: string;
  dueDate: string;
  deliveryQuantity: number;
  status: "active" | "completed";
  temporaryReason: string;
}

export interface CreateIssuanceInput {
  workpieces?: import("./workpieceTracking.js").DispatchWorkpiecesInput;
  /** Server-derived learning metadata; not accepted from public API bodies. */
  allocation?: WorkAllocation;
  projectId: string;
  subtaskId: string;
  workerId: string;
  quantity: number;
  createdBy: string;
  /** 운영 화면에서 가용 수량·선행 조건 예외를 확인한 관리자 불출입니다. */
  confirmedAvailabilityOverride?: boolean;
  /**
   * 이미 외부 운영 시스템에서 확정된 작업을 이중 기록할 때만 사용한다.
   * 프로젝트·작업 소속과 활성 작업자 검증은 유지하고, 로컬 가용 수량 검증만 생략한다.
   */
  bypassAvailabilityValidation?: boolean;
  /** 외부 시스템 작업을 미러링할 때 사용하는 원본 식별자다. */
  sourceSystem?: string;
  sourceId?: string;
  /** 같은 원본을 이관과 실시간 연동이 공유하도록 고정한 정산 식별자다. */
  settlementOriginId?: string;
}

export interface CreateWorkReservationInput {
  workpieces?: import("./workpieceTracking.js").DispatchWorkpiecesInput;
  projectId: string;
  subtaskId: string;
  workerId: string;
  quantity: number;
  createdBy: string;
}

export interface CreateReturnInput {
  returnStorage?: import("./returnStorage.js").ReturnStorageInput;
  workpieces?: import("./workpieceTracking.js").ReturnWorkpiecesInput;
  issuanceId: string;
  workerId: string;
  quantity: number;
  defectiveQuantity?: number;
  deferInspection?: boolean;
  /** 과거 수정 회수 API와의 호환을 위한 직전 검수 회수 ID입니다. */
  correctionOfReturnId?: string;
}

export interface CreateWorkRecordInput {
  workpieces?: { dispatch: import("./workpieceTracking.js").DispatchWorkpiecesInput; return?: Partial<import("./workpieceTracking.js").ReturnWorkpiecesInput> };
  projectId: string;
  subtaskId: string;
  workerId: string;
  quantity: number;
  issuedAt: string;
  returnedAt?: string;
}

export interface UpdateWorkRecordInput {
  workpieceAmendment?: { mode: "reconcile" | "record_only"; previewToken: string; reason: string };
  workerId: string;
  quantity: number;
  issuedAt: string;
  returns: Array<{
    id: string;
    quantity: number;
    returnedAt: string;
    normalQuantity?: number;
    defectiveQuantity?: number;
  }>;
}

export interface CreateInspectionInput {
  returnId: string;
  returnStorage?: import("./returnStorage.js").ReturnStorageInput;
  /* 선택이다 — 칸이 nullable이고 기존 기록도 전부 비어 있다. 회수한 사람을 넣던 것은
     거짓이었다: 회수는 재봉한 사람이, 검수는 나중에 다른 사람이 한다. */
  inspectorWorkerId?: string;
  normalQuantity: number;
  /** 수정가능 — 같은 작업자에게 수정으로 돌아가는 수량. */
  defectiveQuantity: number;
  /** 불량 — 고칠 수 없는 수량. 정산 정책과는 별도로 기록한다. */
  scrapQuantity?: number;
  /** 막대 검수: 같은 불출의 검수 대기 회수를 전부 한 번에 — 수량은 그 합계(2026-10-20). */
  wholeIssuance?: boolean;
}

export interface CreatePlanRunInput {
  projectId: string;
  name: string;
  startDate: string;
  endDate: string;
  createdBy: string;
}

export interface CreatePlanItemInput {
  allocation?: WorkAllocation;
  workDate: string;
  workerId: string;
  subtaskId: string;
  quantity: number;
  instruction: string;
  startAt?: string;
  endAt?: string;
  plannedMinutes?: number;
  workSegments?: Array<{ startAt: string; endAt: string }>;
}

export interface UpdatePlanItemInput {
  workerId: string;
  quantity: number;
  instruction: string;
  startAt: string;
  endAt: string;
}

export interface StartPlanItemInput {
  /** Actual handover quantity; omitted callers retain the planned quantity. */
  quantity?: number;
  workpieces?: import("./workpieceTracking.js").DispatchWorkpiecesInput;
  createdBy: string;
  confirmationKey?: string;
}

export interface PlanDispatchCheck {
  planItemId: string;
  projectName: string;
  taskName: string;
  workerName: string;
  quantity: number;
  plannedStartAt: string;
  availableQuantity: number;
  warnings: Array<{ kind: "early" | "not_ready" | "unknown_readiness"; message: string }>;
  /** Fingerprint of the exact plan and preparation state that the operator reviewed. */
  confirmationKey: string;
}

export interface StartPlanItemResult extends WorkMutationResult {
  issuanceId: string;
  planItem: WorkPlanItem;
}

export interface CreateAutomaticPlanInput {
  projectId: string;
  subtaskId: string;
  startDate: string;
  quantity: number;
  workerIds?: string[];
  name?: string;
  createdBy: string;
}

export interface CreateOptimizedPlanInput {
  projectIds: string[];
  startDate: string;
  name?: string;
  createdBy: string;
}

export interface OptimizedPlanResult {
  replanningDeferred?: boolean;
  id: string;
  plannedQuantity: number;
  endDate: string;
  estimatedCompletionAt: string;
  calculationMs: number;
  algorithm: string;
}

export type UpdateWorkerTaskEfficiencyInput = {
  workerId: string;
  subtaskId: string;
  updatedBy: string;
} & ({ unitsPerHour: number; smvMinutes?: never } | { smvMinutes: number; unitsPerHour?: never });

export interface CreatePartTimeSettlementInput {
  workerId: string;
  workDate: string;
  /* 금액만 받으면 그 숫자가 어디서 나왔는지 줄에 남지 않는다 — 8시간 × 12,000을 사람이
     암산해 96,000만 적으면, 나중에 그 줄만 보고는 맞는지 셀 수가 없다. */
  startTime?: string;
  endTime?: string;
  breakMinutes?: number;
  hourlyWage?: number;
  amount: number;
  description: string;
  subtaskName?: string;
  createdBy: string;
}

export interface CreateSettlementContractInput {
  workerId: string;
  title: string;
  subtaskName?: string;
  startDate: string;
  endDate: string;
  settlementDate?: string;
  calculationType: ContractCalculationType;
  quantity: number;
  unitAmount: number;
  description: string;
  createdBy: string;
}

/** 장치 인증으로 조회하는 완성 작업 기록. 개인정보·정산 정보는 포함하지 않는다. */
export interface FinishTabletRecord {
  id: string;
  projectName: string;
  subtaskName: string;
  recordedAt: string;
  quantity: number;
  defectiveQuantity: number;
}
